/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable, NgZone } from "@angular/core";
import { UtilityService } from "./utility.service";
import { FirebaseFirestoreService } from "./firebase.firestore.service";
import * as turf from '@turf/turf';
import { Feature, Polygon } from 'geojson';
import { folsomGeoJSON, ranchoCordovaGeoJSON } from '../constants';

@Injectable({
  providedIn: 'root'
})
export class LocationService {
    private ngZone = inject(NgZone);
    private utilityService = inject(UtilityService);
    private firebaseFirestoreService = inject(FirebaseFirestoreService);
    
    // Callback to component for showing dialogs
    private showDialogCallback?: (dialogType: 'location-permission' | 'open-settings' | 'folsom-area' | 'rancho-cordova-area' | 'not-supported-city' | 'not-supported-area') => void;
    
    constructor() {}
    
    setDialogCallback(callback: (dialogType: 'location-permission' | 'open-settings' | 'folsom-area' | 'rancho-cordova-area' | 'not-supported-city' | 'not-supported-area') => void) {
      // Only allow one callback to be set at a time
      this.showDialogCallback = callback;
    }
    
    clearDialogCallback() {
      this.showDialogCallback = undefined;
    }

    subscribeToLocationEvents() {
        (window as any).onLocationUpdate = (data: {location: {coords: {latitude: number; longitude: number}, timestamp: number}, permissionResponse: {granted: boolean, status: 'denied' | 'granted' | 'undetermined', canAskAgain: boolean}}) => {
          const { location, permissionResponse}: {location: {coords: {latitude: number; longitude: number}, timestamp: number}, permissionResponse: {granted: boolean, status: 'denied' | 'granted' | 'undetermined', canAskAgain: boolean}} = data;
          this.ngZone.run(() => { 
              console.log('User location', location);
              if (permissionResponse) {
                this.utilityService.locationPermissionStatus = permissionResponse as any;
              }
              this.utilityService.location = {latitude: location.coords.latitude, longitude: location.coords.longitude, timestamp: location.timestamp};
              this.checkIfUserLocationIsInOurOperatingAreas(this.utilityService.location);
          })
        }

        (window as any).onLocationPermissionStatus = (permissions: {granted: boolean, status: 'denied' | 'granted' | 'undetermined', canAskAgain: boolean}) => {
            this.ngZone.run(() => {
                this.utilityService.locationPermissionStatus = permissions as any;
                console.log('Location permission status', this.utilityService.locationPermissionStatus);
                this.askForLocationPermission();
            })
        }
        setTimeout(() => {
            this.getlocationPermissionStatus();
        }, 500);
    }

    getlocationPermissionStatus() {
    if((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(JSON.stringify({action: "get-location-permission-status", data: null}));
    } else {
      navigator.permissions.query({ name: 'geolocation' }).then(result => {
        this.utilityService.locationPermissionStatus = (result.state == 'granted' ? {granted: true, status: 'granted'} : {granted: false, status: result.state, canAskAgain: result.state !== 'denied'}) as any;
        this.askForLocationPermission();
      });
    }
  }

  askForLocationPermission() {
    if(this.utilityService.locationPermissionStatus?.granted) {
      this.getUserLocation();
      return;
    }
    if(!this.utilityService.locationPermissionStatus?.canAskAgain) {
      // Call the component's dialog method if available
      if (this.showDialogCallback) {
        this.showDialogCallback('open-settings');
      } else {
        // Fallback: get user location directly
        this.getUserLocation();
      }
      return;
    }

    localStorage.removeItem('operatingAreaDisclaimerShowed');

    // Call the component's dialog method if available
    if (this.showDialogCallback) {
      this.showDialogCallback('location-permission');
    } else {
      // Fallback: get user location directly
      this.getUserLocation();
    }
  }

  getUserLocation() {
    if((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(JSON.stringify({action: "get-location", data: null}));
    } else {
      navigator.geolocation.getCurrentPosition((location) => {
        console.log('User location', location);
        this.utilityService.locationPermissionStatus = {granted: true, status: 'granted'} as any;
        this.utilityService.location = { latitude: location.coords.latitude, longitude: location.coords.longitude, timestamp: location.timestamp};
        this.firebaseFirestoreService.updateUserDocument({ currentLocation: this.utilityService.location});
        this.checkIfUserLocationIsInOurOperatingAreas(this.utilityService.location);
      }, () => {
        this.utilityService.locationPermissionStatus = {granted: false, status: 'denied'} as any;

      });
    }
  }

  checkIfUserLocationIsInOurOperatingAreas(location: {latitude: number; longitude: number}) {
    const userPoint = turf.point([location.longitude, location.latitude]);      
    const folsomPolygonFeature = turf.feature(turf.geometry('Polygon', folsomGeoJSON.geometry.coordinates)) as Feature<Polygon>;
    const ranchoPolygonFeature = turf.feature(turf.geometry('Polygon', ranchoCordovaGeoJSON.geometry.coordinates)) as Feature<Polygon>;

    const isInFolsom = turf.booleanPointInPolygon(userPoint, folsomPolygonFeature);
    const isInRanchoCordova = turf.booleanPointInPolygon(userPoint, ranchoPolygonFeature);

    const disclaimerShowed = localStorage.getItem('operatingAreaDisclaimerShowed');
    if (isInFolsom && !disclaimerShowed) {
      console.log("User is in Folsom");
      this.utilityService.isUserInOperatingArea = true;
      if(disclaimerShowed !== 'true') {
        // Call the component's dialog method if available
        if (this.showDialogCallback) {
          this.showDialogCallback('folsom-area');
        }
      }
    } else if (isInRanchoCordova && !disclaimerShowed) {
      console.log("User is in Rancho Cordova");
      this.utilityService.isUserInOperatingArea = true;
      if(disclaimerShowed !== 'true') {
        // Call the component's dialog method if available
        if (this.showDialogCallback) {
          this.showDialogCallback('rancho-cordova-area');
        }
      }
    } else if(!disclaimerShowed) {
      console.log("User is not in a supported city");
      this.utilityService.isUserInOperatingArea = false;
      // Call the component's dialog method if available
      if (this.showDialogCallback) {
        this.showDialogCallback('not-supported-city');
      }
    } else if(disclaimerShowed && !isInFolsom && !isInRanchoCordova) {
      // Call the component's dialog method if available
      if (this.showDialogCallback) {
        this.showDialogCallback('not-supported-area');
      }
    }
    localStorage.setItem('operatingAreaDisclaimerShowed', JSON.stringify(true));
  }
}